import { useState } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const industriesPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Industries We Serve | AI Automation for Salons, Clinics, Trades & More',
  description: 'Everything Automation builds AI booking and communication systems for hair salons, dental clinics, builders, fitness studios, home services, and physio practices across the UK.',
  url: `${siteUrl}/industries`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
  },
  about: [
    { '@type': 'Thing', name: 'Hair Salon Booking Automation' },
    { '@type': 'Thing', name: 'Dental Clinic Scheduling Automation' },
    { '@type': 'Thing', name: 'Builders and Trades Lead Automation' },
    { '@type': 'Thing', name: 'Fitness Studio Booking Automation' },
    { '@type': 'Thing', name: 'Home Services Scheduling Automation' },
    { '@type': 'Thing', name: 'Physiotherapy Practice Automation' },
  ],
};

const industries = [
  {
    id: 'hair-beauty',
    label: 'Hair & Beauty',
    icon: 'ri-scissors-line',
    headline: 'Fill Every Chair. Eliminate Empty Slots.',
    subheadline: 'Hair salons, barbershops, lash studios, and beauty therapists lose thousands every year to missed enquiries and no-shows. Our AI fixes both, automatically.',
    image: 'https://public.readdy.ai/ai/img_res/73d13ad927b1bc983554c8af32a7120e.jpg',
    recommendedPlan: {
      name: 'Pro',
      outcome: 'Turn more enquiries into booked appointments',
      monthly: '£299/mo',
      setup: 'Setup: £399 – £699',
    },
    stats: [
      { value: '87%', label: 'of new salon clients decide within 10 minutes of enquiring' },
      { value: '3.4x', label: 'more bookings from instant AI responses vs manual replies' },
      { value: '40%', label: 'reduction in no-shows with automated reminders' },
    ],
    useCases: [
      {
        icon: 'ri-chat-3-line',
        title: 'Instant Enquiry Response',
        desc: 'Someone DMs your salon at 11pm asking about highlights. The AI replies instantly with availability, pricing, and a booking link. You wake up to a confirmed appointment.',
      },
      {
        icon: 'ri-calendar-check-line',
        title: 'No-Show Prevention',
        desc: 'Automated reminders go out 48 hours and 2 hours before every appointment. Clients confirm with one tap. No-shows drop, your diary stays full.',
      },
      {
        icon: 'ri-repeat-line',
        title: 'Rebook Lapsed Clients',
        desc: 'AI detects clients who haven\'t been in for 6-8 weeks and sends a personalised rebook nudge. Turns forgotten clients into regulars again.',
      },
      {
        icon: 'ri-star-line',
        title: 'Review Requests',
        desc: 'After every appointment, a follow-up message goes out thanking the client and requesting a review. Your Google rating climbs without you lifting a finger.',
      },
    ],
    workflow: [
      'Client enquires via Instagram, Google, or your website',
      'AI responds instantly with availability and pricing',
      'Booking is confirmed and added to your calendar',
      'Reminder sent 48h before, reducing no-shows',
    ],
    cta: 'Fill My Salon Diary',
  },
  {
    id: 'dental-clinics',
    label: 'Dental & Clinics',
    icon: 'ri-tooth-line',
    headline: 'Keep Your Appointment Book Full Without the Admin.',
    subheadline: 'Dental practices, GP clinics, and aesthetics studios waste huge amounts of staff time managing inbound enquiries. Let AI handle it 24/7 while your team focuses on patients.',
    image: 'https://readdy.ai/api/search-image?query=modern%20dental%20clinic%20reception%20area%20clean%20white%20interior%20professional%20medical%20office%20dark%20background%20with%20soft%20lighting%20calm%20clinical%20atmosphere%20premium%20healthcare%20facility&width=800&height=500&seq=industry-dental&orientation=landscape',
    recommendedPlan: {
      name: 'Pro',
      outcome: 'Turn more enquiries into booked appointments',
      monthly: '£299/mo',
      setup: 'Setup: £399 – £699',
    },
    stats: [
      { value: '60%', label: 'of patients choose a provider based on how quickly they respond' },
      { value: '5hrs', label: 'average admin time saved per week per clinic' },
      { value: '2x', label: 'more new patients converted from enquiry to first appointment' },
    ],
    useCases: [
      {
        icon: 'ri-user-add-line',
        title: 'New Patient Intake',
        desc: 'A new patient finds your clinic on Google and sends a message. AI captures their details, explains your services, and books their first appointment - no receptionist needed.',
      },
      {
        icon: 'ri-calendar-2-line',
        title: 'Recall & Re-attendance',
        desc: 'AI automatically chases patients due for their 6-month check-up or follow-up appointment, keeping your diary full with recall bookings you\'d otherwise miss.',
      },
      {
        icon: 'ri-file-list-3-line',
        title: 'Pre-Appointment Admin',
        desc: 'Forms, pre-screening questions, and consent information are sent and collected automatically before the patient arrives. Less time at reception, smoother appointments.',
      },
      {
        icon: 'ri-message-2-line',
        title: 'Out-of-Hours Enquiries',
        desc: 'Most clinic enquiries come outside office hours. AI handles them all - answering questions, booking slots, and filtering genuine leads from general queries.',
      },
    ],
    workflow: [
      'Patient enquires via website, Google, or social media',
      'AI qualifies the enquiry and captures details',
      'Appointment is scheduled directly into your system',
      'Pre-appointment comms and reminders sent automatically',
    ],
    cta: 'Automate My Clinic Bookings',
  },
  {
    id: 'builders-trades',
    label: 'Builders & Trades',
    icon: 'ri-hammer-line',
    headline: 'Turn Quote Requests Into Confirmed Jobs.',
    subheadline: 'Builders, plumbers, electricians, and roofers lose work every day to slow follow-ups. Our AI responds to every lead instantly, books site visits, and chases quotes until they convert.',
    image: 'https://readdy.ai/api/search-image?query=professional%20construction%20builder%20working%20on%20modern%20home%20renovation%20dark%20moody%20cinematic%20lighting%20tools%20equipment%20premium%20quality%20tradespeople%20worksite%20aesthetic&width=800&height=500&seq=industry-builders&orientation=landscape',
    recommendedPlan: {
      name: 'Scale',
      outcome: 'Run your entire lead and booking system on autopilot',
      monthly: '£599/mo',
      setup: 'Setup: £799 – £1,499',
    },
    stats: [
      { value: '70%', label: 'of homeowners hire the first tradesperson to respond' },
      { value: '8x', label: 'more jobs won when following up within 5 minutes vs 30 minutes' },
      { value: '£25-30k', label: 'average additional annual revenue for trades clients on our system' },
    ],
    useCases: [
      {
        icon: 'ri-search-eye-line',
        title: 'Lead Qualification',
        desc: 'Not every enquiry is worth your time. AI asks the right questions upfront - job type, location, budget range, timeline - so you only spend time on qualified leads.',
      },
      {
        icon: 'ri-map-pin-line',
        title: 'Site Visit Booking',
        desc: 'Qualified leads are automatically offered your available site visit slots. They pick a time, it\'s in your calendar. No back-and-forth, no phone tag.',
      },
      {
        icon: 'ri-mail-check-line',
        title: 'Quote Follow-ups',
        desc: 'Sent a quote and heard nothing? AI sends a series of friendly follow-up messages over 7 days. Most jobs that were going cold are recovered.',
      },
      {
        icon: 'ri-award-line',
        title: 'Review Collection',
        desc: 'After every job completion, AI requests a review from the homeowner. Builds your Google and Checkatrade profile without any effort from you.',
      },
    ],
    workflow: [
      'Homeowner submits enquiry via website or social',
      'AI qualifies the lead with targeted questions',
      'Site visit is booked into your available slots',
      'Quote follow-ups sent automatically until converted',
    ],
    cta: 'Win More Jobs Automatically',
  },
  {
    id: 'fitness-wellness',
    label: 'Fitness & Wellness',
    icon: 'ri-heart-pulse-line',
    headline: 'Keep Your Schedule Packed. Reduce Drop-offs.',
    subheadline: 'Personal trainers, yoga studios, gyms, and wellness coaches live and die by their booking rates. AI keeps your classes full, members engaged, and drop-off rates low.',
    image: 'https://readdy.ai/api/search-image?query=modern%20fitness%20gym%20studio%20dark%20moody%20dramatic%20lighting%20equipment%20weights%20clean%20minimal%20professional%20athletic%20premium%20fitness%20club%20aesthetic&width=800&height=500&seq=industry-fitness&orientation=landscape',
    recommendedPlan: {
      name: 'Pro',
      outcome: 'Turn more enquiries into booked appointments',
      monthly: '£299/mo',
      setup: 'Setup: £399 – £699',
    },
    stats: [
      { value: '35%', label: 'average increase in class attendance with automated reminders' },
      { value: '55%', label: 'of gym leads who don\'t hear back within 1 hour go elsewhere' },
      { value: '4x', label: 'higher retention rate with automated check-in and follow-up sequences' },
    ],
    useCases: [
      {
        icon: 'ri-calendar-todo-line',
        title: 'Class & Session Booking',
        desc: 'New clients enquire via Instagram or your website and AI instantly books them into the next available class or 1-2-1 slot, with a confirmation and location details.',
      },
      {
        icon: 'ri-group-line',
        title: 'Membership Renewals',
        desc: 'AI detects when a membership is coming up for renewal and starts a gentle automated sequence to re-engage. Fewer lapses, more consistent revenue.',
      },
      {
        icon: 'ri-bar-chart-line',
        title: 'Progress Check-ins',
        desc: 'Automated check-in messages at week 2, week 6, and month 3 keep clients motivated and engaged. Higher engagement = longer retention.',
      },
      {
        icon: 'ri-notification-3-line',
        title: 'Class Reminders',
        desc: 'Nobody forgets a class when AI sends a reminder 2 hours before. Attendance rates go up, last-minute cancellations go down.',
      },
    ],
    workflow: [
      'Prospect enquires about classes or membership',
      'AI answers questions and books their first session',
      'Automated onboarding sequence starts post-booking',
      'Ongoing check-ins and renewal prompts keep them engaged',
    ],
    cta: 'Fill My Classes Automatically',
  },
  {
    id: 'home-services',
    label: 'Home Services',
    icon: 'ri-home-heart-line',
    headline: 'Book More Jobs. Chase Zero Leads Manually.',
    subheadline: 'Cleaners, gardeners, window cleaners, and property maintenance businesses grow through repeat bookings and referrals. AI handles your bookings and keeps clients coming back.',
    image: 'https://readdy.ai/api/search-image?query=professional%20home%20cleaning%20service%20modern%20bright%20clean%20house%20interior%20minimalist%20living%20room%20premium%20quality%20domestic%20cleaning%20service%20aesthetic%20dark%20background&width=800&height=500&seq=industry-home-services&orientation=landscape',
    recommendedPlan: {
      name: 'Starter',
      outcome: 'Capture leads you would normally miss',
      monthly: '£149/mo',
      setup: 'Setup: £199 – £299',
    },
    stats: [
      { value: '92%', label: 'of recurring service clients stay if they receive consistent follow-up' },
      { value: '3x', label: 'more referrals generated with a structured review request system' },
      { value: '6hrs', label: 'average weekly time saved on booking management per operator' },
    ],
    useCases: [
      {
        icon: 'ri-calendar-check-line',
        title: 'Recurring Job Scheduling',
        desc: 'Weekly, fortnightly, or monthly jobs get auto-confirmed and reminded. Clients never forget, you never have an empty day.',
      },
      {
        icon: 'ri-user-star-line',
        title: 'New Client Onboarding',
        desc: 'First-time enquiries get a fast, friendly response with pricing, availability, and a booking link. You gain a new client without a single phone call.',
      },
      {
        icon: 'ri-share-line',
        title: 'Referral Requests',
        desc: 'After a successful job, AI sends a personalised message asking satisfied clients to refer a friend. Word-of-mouth growth on autopilot.',
      },
      {
        icon: 'ri-time-line',
        title: 'Arrival Window Confirmation',
        desc: 'Day-before confirmations with arrival window details mean clients are prepared, leading to smoother jobs and happier customers.',
      },
    ],
    workflow: [
      'New client enquires via Google, social, or word of mouth',
      'AI responds with availability and books the first job',
      'Recurring schedule set up automatically if applicable',
      'Review and referral requests sent after every visit',
    ],
    cta: 'Automate My Home Service Bookings',
  },
  {
    id: 'physio-therapy',
    label: 'Physio & Therapy',
    icon: 'ri-stethoscope-line',
    headline: 'Remove the Admin. Focus on Your Patients.',
    subheadline: 'Physiotherapists, osteopaths, and therapists spend too much time on administrative tasks. AI takes over new patient intake, appointment scheduling, and follow-up so you can do what you do best.',
    image: 'https://readdy.ai/api/search-image?query=professional%20physiotherapy%20clinic%20treatment%20room%20calm%20serene%20minimal%20modern%20equipment%20clean%20neutral%20tones%20dark%20elegant%20premium%20healthcare%20practice%20interior&width=800&height=500&seq=industry-physio&orientation=landscape',
    recommendedPlan: {
      name: 'Pro',
      outcome: 'Turn more enquiries into booked appointments',
      monthly: '£299/mo',
      setup: 'Setup: £399 – £699',
    },
    stats: [
      { value: '68%', label: 'of therapy clients rebook if they receive a timely follow-up message' },
      { value: '2hrs', label: 'average daily admin time eliminated per therapist' },
      { value: '45%', label: 'increase in new patient conversions with instant response systems' },
    ],
    useCases: [
      {
        icon: 'ri-user-add-line',
        title: 'New Patient Enquiries',
        desc: 'Someone finds you online after injuring themselves. AI responds immediately, explains your process, and books an initial assessment before they look elsewhere.',
      },
      {
        icon: 'ri-heart-add-line',
        title: 'Treatment Plan Follow-ups',
        desc: 'After each session, AI sends a structured follow-up to check progress and prompt the next appointment booking. Better continuity, better outcomes.',
      },
      {
        icon: 'ri-file-text-line',
        title: 'Pre-Session Intake',
        desc: 'New patient forms and health questionnaires collected before the first session. You have everything you need before they walk in the door.',
      },
      {
        icon: 'ri-refresh-line',
        title: 'Discharge & Return',
        desc: 'Clients who complete a treatment plan receive a check-in 8-12 weeks later. Many return for maintenance sessions they wouldn\'t have otherwise booked.',
      },
    ],
    workflow: [
      'Patient enquires via website or referral',
      'AI captures details and books initial assessment',
      'Pre-appointment forms sent and collected automatically',
      'Post-session follow-ups and rebooking handled by AI',
    ],
    cta: 'Reduce My Admin Load',
  },
];

const IndustriesPage = () => {
  const [activeTab, setActiveTab] = useState('hair-beauty');
  const active = industries.find((i) => i.id === activeTab)!;

  return (
    <>
      <title>Industries We Serve | AI Automation for Salons, Clinics, Trades &amp; More | Everything Automation</title>
      <meta name="description" content="Everything Automation builds AI booking and communication systems for hair salons, dental clinics, builders, fitness studios, home services, and physio practices across the UK. See how we transform each industry." />
      <meta name="keywords" content="AI automation industries, hair salon booking automation, dental clinic scheduling, builders AI automation, fitness studio booking system" />
      <link rel="canonical" href={`${siteUrl}/industries`} />
      <meta property="og:title" content="Industries We Serve | AI Automation for Salons, Clinics, Trades &amp; More" />
      <meta property="og:description" content="AI booking and communication automation built specifically for your industry. Hair salons, dental clinics, builders, fitness studios, home services, physio and more." />
      <meta property="og:url" content={`${siteUrl}/industries`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(industriesPageSchema) }} />
      <main className="bg-ea-black">
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20dark%20premium%20background%20with%20glowing%20electric%20light%20streaks%20multiple%20industry%20symbols%20silhouettes%20minimal%20tech%20aesthetic%20deep%20black%20cinematic%20modern%20design&width=1920&height=700&seq=industries-hero-bg&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-ea-black/60 via-ea-black to-ea-black"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Built for Appointment-Based Businesses</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Industries We{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Transform
              </span>
            </h1>
            <p className="text-ea-gray text-lg lg:text-xl max-w-2xl mx-auto mb-8">
              If your business runs on bookings and appointments, we built this for you. See exactly how automation works for your sector.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {industries.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => setActiveTab(ind.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                    activeTab === ind.id
                      ? 'bg-ea-blue text-white'
                      : 'bg-ea-dark-card border border-ea-dark-border text-ea-gray hover:border-ea-blue/40 hover:text-white'
                  }`}
                >
                  <i className={`${ind.icon} text-xs`}></i>
                  {ind.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Deep-Dive */}
        <section className="pb-24" id={active.id}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            {/* Industry Header */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-ea-blue/10 border border-ea-blue/20 mb-5">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${active.icon} text-ea-blue text-xs`}></i>
                  </div>
                  <span className="text-ea-blue text-xs font-medium">{active.label}</span>
                </div>
                <h2 className="text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">{active.headline}</h2>
                <p className="text-ea-gray text-lg leading-relaxed mb-8">{active.subheadline}</p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3.5 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)' }}
                >
                  {active.cta} <i className="ri-arrow-right-line"></i>
                </Link>

                {/* Recommended Plan Callout */}
                <div className="bg-ea-dark-card border border-ea-blue/25 rounded-xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 flex-shrink-0">
                    <i className="ri-price-tag-3-line text-ea-blue text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-ea-blue text-xs font-bold uppercase tracking-wider">Recommended Plan</span>
                      <span className="px-2.5 py-0.5 bg-ea-blue/15 border border-ea-blue/30 rounded-full text-ea-blue text-xs font-bold">{active.recommendedPlan.name}</span>
                    </div>
                    <p className="text-white/80 text-xs leading-snug mb-1">{active.recommendedPlan.outcome}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      <span className="inline-flex items-center gap-1.5 text-white text-xs font-semibold">
                        <i className="ri-calendar-line text-ea-blue text-xs"></i>
                        {active.recommendedPlan.monthly}
                      </span>
                      <span className="text-ea-dark-border">|</span>
                      <span className="inline-flex items-center gap-1.5 text-ea-gray text-xs">
                        <i className="ri-tools-line text-ea-blue text-xs"></i>
                        {active.recommendedPlan.setup}
                      </span>
                    </div>
                  </div>
                  <Link
                    to="/pricing"
                    className="text-ea-blue text-xs font-semibold hover:underline whitespace-nowrap flex items-center gap-1 cursor-pointer"
                  >
                    See full plan <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden h-80 lg:h-96">
                <img
                  src={active.image}
                  alt={active.label}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-16">
              {active.stats.map((stat) => (
                <div key={stat.value} className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-4 sm:p-6 text-center">
                  <div className="text-2xl sm:text-4xl font-bold text-ea-blue mb-1 sm:mb-2">{stat.value}</div>
                  <p className="text-ea-gray text-xs sm:text-sm leading-relaxed">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Use Cases */}
            <div className="mb-16">
              <div className="text-center mb-10">
                <h3 className="text-2xl font-bold text-white mb-2">How It Works for {active.label}</h3>
                <p className="text-ea-gray text-sm">Real automation scenarios built specifically for your business type.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-6">
                {active.useCases.map((uc) => (
                  <div key={uc.title} className="bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/40 rounded-xl p-3 sm:p-6 transition-all duration-300 cursor-default group">
                    <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
                      <div className="w-8 h-8 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20 group-hover:bg-ea-blue/20 transition-colors duration-300 shrink-0">
                        <i className={`${uc.icon} text-ea-blue text-sm sm:text-lg`}></i>
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-xs sm:text-sm mb-1 sm:mb-2">{uc.title}</h4>
                        <p className="text-ea-gray text-[11px] sm:text-sm leading-relaxed">{uc.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Workflow */}
            <div className="bg-ea-navy rounded-2xl border border-ea-dark-border p-8 lg:p-12 mb-16">
              <div className="text-center mb-10">
                <h3 className="text-2xl font-bold text-white mb-2">Your Automation Workflow</h3>
                <p className="text-ea-gray text-sm">From first enquiry to confirmed booking, handled automatically.</p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {active.workflow.map((step, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center relative">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-ea-blue/20 border border-ea-blue/40 text-ea-blue font-bold text-sm mb-3 sm:mb-4 flex-shrink-0">
                      {idx + 1}
                    </div>
                    <p className="text-white text-xs sm:text-sm leading-relaxed font-medium">{step}</p>
                    {idx < active.workflow.length - 1 && (
                      <div className="hidden md:block absolute top-5 left-[calc(50%+20px)] right-0 h-px bg-ea-blue/20 w-[calc(100%-20px)]"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Other Industries Quick Links */}
            <div>
              <p className="text-ea-gray text-sm text-center mb-6">Explore other industries</p>
              <div className="flex flex-wrap justify-center gap-3">
                {industries.filter((i) => i.id !== activeTab).map((ind) => (
                  <button
                    key={ind.id}
                    onClick={() => { setActiveTab(ind.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-ea-dark-border text-ea-gray hover:border-ea-blue/50 hover:text-white text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${ind.icon} text-ea-blue text-xs`}></i>
                    {ind.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-ea-navy">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-5">
              Ready to see it work for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                your business?
              </span>
            </h2>
            <p className="text-ea-gray text-lg mb-8">
              Book a free demo and we&apos;ll show you exactly how automation would work for your specific business type.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-ea-blue hover:bg-ea-blue-light text-white font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-base"
              style={{ boxShadow: '0 0 25px rgba(14, 165, 233, 0.3)' }}
            >
              Book a Free Demo <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default IndustriesPage;
